import { useState } from 'react';
import { updateEvent } from '../services/api/eventService';

/**
 * Custom hook for managing event notes
 * @param {Object} event - Event object
 * @param {Function} onClose - Callback to close dialog
 * @param {Function} setSnackbar - Callback to show snackbar
 * @returns {Object} Note management state and handlers
 */
export function useEventNotes(event, onClose, setSnackbar) {
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);

  const existingNotes = event?.metadata?.notes || [];
  const isMatchEvent = event?.eventType === 'match';

  const handleEditNote = (index) => {
    setEditingNoteIndex(index);
    setNoteText(existingNotes[index].text);
  };

  const handleCancelEdit = () => {
    setEditingNoteIndex(null);
    setNoteText('');
  };

  const handleDeleteNote = async (index) => {
    setSaving(true);
    try {
      // Remove the note at the specified index
      const updatedNotes = existingNotes.filter((_, i) => i !== index);

      // Build the updated metadata object based on event type
      let updatedMetadata = { notes: updatedNotes };

      if (isMatchEvent) {
        updatedMetadata = {
          personName: event.metadata.personName,
          personImage: event.metadata.personImage,
          matchSource: event.metadata.matchSource,
          notes: updatedNotes
        };

        if (event.metadata.person) {
          updatedMetadata.person = event.metadata.person;
        }
        if (event.metadata.openAssignment) {
          updatedMetadata.openAssignment = event.metadata.openAssignment;
        }
        if (event.metadata.matchAccuracy !== undefined) {
          updatedMetadata.matchAccuracy = event.metadata.matchAccuracy;
        }
        if (event.metadata.capturedImageUrl) {
          updatedMetadata.capturedImageUrl = event.metadata.capturedImageUrl;
        }
      }

      await updateEvent(event.eventId, {
        metadata: updatedMetadata
      });

      setSnackbar('Note deleted successfully', 'success');

      // Update the local event object
      if (!event.metadata) event.metadata = {};
      event.metadata.notes = updatedNotes;

      // If we were editing this note, cancel the edit
      if (editingNoteIndex === index) {
        setEditingNoteIndex(null);
        setNoteText('');
      }

      // Close dialog after short delay
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Failed to delete note:', error);
      setSnackbar('Failed to delete note: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNote = async () => {
    if (!noteText.trim()) {
      setSnackbar('Please enter a note', 'warning');
      return;
    }

    setSaving(true);
    try {
      let updatedNotes;

      if (editingNoteIndex !== null) {
        // Update existing note
        updatedNotes = [...existingNotes];
        updatedNotes[editingNoteIndex] = {
          ...updatedNotes[editingNoteIndex],
          text: noteText,
          updatedAt: new Date().toISOString()
        };
      } else {
        // Create new note with timestamp
        const newNote = {
          text: noteText,
          createdAt: new Date().toISOString()
        };
        // Add new note to existing notes array
        updatedNotes = [...existingNotes, newNote];
      }

      // Build the updated metadata object based on event type
      let updatedMetadata = { notes: updatedNotes };

      if (isMatchEvent) {
        updatedMetadata = {
          personName: event.metadata.personName,
          personImage: event.metadata.personImage,
          matchSource: event.metadata.matchSource,
          notes: updatedNotes
        };

        // Include person and openAssignment if they exist
        if (event.metadata.person) {
          updatedMetadata.person = event.metadata.person;
        }
        if (event.metadata.openAssignment) {
          updatedMetadata.openAssignment = event.metadata.openAssignment;
        }
        if (event.metadata.matchAccuracy !== undefined) {
          updatedMetadata.matchAccuracy = event.metadata.matchAccuracy;
        }
        if (event.metadata.capturedImageUrl) {
          updatedMetadata.capturedImageUrl = event.metadata.capturedImageUrl;
        }
      }

      // Update the event with new notes array
      await updateEvent(event.eventId, {
        metadata: updatedMetadata
      });

      setSnackbar(editingNoteIndex !== null ? 'Note updated successfully' : 'Note saved successfully', 'success');
      setNoteText('');
      setEditingNoteIndex(null);

      // Update the local event object to show the new note immediately
      if (!event.metadata) event.metadata = {};
      event.metadata.notes = updatedNotes;

      // Close dialog after short delay
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Failed to save note:', error);
      console.error('Error details:', error.response?.data);
      setSnackbar('Failed to save note: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  return {
    noteText,
    setNoteText,
    saving,
    editingNoteIndex,
    existingNotes,
    handleEditNote,
    handleCancelEdit,
    handleDeleteNote,
    handleSaveNote
  };
}
